import { useCallback, useMemo } from "react"

import { format } from "date-fns"
import { Formik } from "formik"
import { isEqual } from "lodash"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { L2RButton, ScrollableLayout, Typography, useIsMobileDevice } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../constants/i18n"
import { useDocumentProject } from "../../hooks/queries/useDocumentProject"
import { useDocumentTypes } from "../../hooks/queries/useDocumentTypes"

import { validationSchema } from "./DocumentForm.schema"
import * as Styled from "./DocumentForm.styled"

const MAX_FILE_SIZE_MB = 20
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024
const DATE_FORMAT = "yyyy-MM-dd"
const MAX_FILENAME_LENGTH = 100

export const DocumentForm = (props) => {
    const {
        className,
        document,
        isSubmitting,
        onCancel,
        onSubmit,
        onChange,
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobile = useIsMobileDevice()

    const {
        data: project,
        isLoading: isLoadingPoject,
        isError: isErrorProject,
    } = useDocumentProject()
    const {
        data: fileTypes,
        isLoading: isLoadingFileTypes,
        isError: isErrorFileTypes,
    } = useDocumentTypes()

    const initialValues = useMemo(() => {
        const values = document ? { ...document } : {
            file: null,
            filename: "",
            document_type: "",
            date: new Date(),
            tags: [],
        }
        if (document) {
            values.file = {
                file: document.file,
                filename: document.filename,
                mime_type: document.mime_type,
            }
            values.date = new Date(document.date)
        }
        return values
    }, [document])

    const handleFieldChange = useCallback((fieldsValues, values) => {
        const newValues = {
            ...values,
            ...fieldsValues,
        }

        const areInitialValues = isEqual(newValues, initialValues)
        onChange(!areInitialValues)
    }, [initialValues, onChange])

    const handleDropFile = (files, setFieldValue, values) => {
        if (files.length > 0) {
            const file = files[0]
            const filenameParts = file.name.split(".")
            const extension = filenameParts.length >= 2 ? filenameParts.pop() : ""
            const name = filenameParts.join(".").substring(0, MAX_FILENAME_LENGTH - (extension.length + 1))
            const fullName = `${name}${extension ? "." + extension : ""}`

            setFieldValue("file", file, false)
            setFieldValue("filename", fullName, true)
            handleFieldChange({ filename: fullName, file: file }, values)
        } else {
            setFieldValue("file", null)
            setFieldValue("filename", "")
            handleFieldChange({ filename: "", file: null }, values)
        }
    }

    const updateFieldValue = useCallback((field, value, setFieldValue, values) => {
        setFieldValue(field, value, false)

        handleFieldChange({ [field]: value }, values)
    }, [handleFieldChange])

    const handleDateChange = useCallback((date, context, setFieldValue, values, setFieldError) => {
        setFieldValue("date", date, false)
        if (!context.validationError) {
            handleFieldChange({ date: date }, values)
            setFieldError("date", null)
        } else {
            setFieldError("date", context.validationError)
        }
    }, [handleFieldChange])

    const handleSubmit = useCallback((values) => {
        onSubmit({
            ...values,
            date: values.date ? format(values.date, DATE_FORMAT) : "",
        })
    }, [onSubmit])

    const typeOptions = useMemo(() => {
        return (fileTypes || []).sort((typeA, typeB) => {
            if (typeA.order !== typeB.order) {
                return typeA.order - typeB.order
            }
            return typeA.name.localeCompare(typeB.name)
        })
            .map(type => <Styled.TypeMenuItem
                key={type.name}
                value={type.name}>
                {type.icon && <Styled.TypeIcon src={type.icon} alt={type.name} />}
                <Typography variant="Desktop & Mobile/Body/Regular" noWrap>{type.name}</Typography>
            </Styled.TypeMenuItem>)
    }, [fileTypes])

    const fileSizeValidator = useCallback((file) => {
        if (file.size > MAX_FILE_SIZE) {
            return {
                code: "file-too-large",
                message: t(I18N_NAMESPACE, "forms.documentAddForm.fileTooLarge", { maxSize: MAX_FILE_SIZE_MB }),
            }
        }

        return null
    }, [t])

    if (isLoadingFileTypes || isErrorFileTypes || isLoadingPoject || isErrorProject) {
        return null
    }

    return <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema(t)}
    >
        {({ errors, values, setFieldValue, setFieldError }) => {
            const hasErrors = Object.values(errors).some(error => !!error)
            const nameIsEmpty = !values.filename || values.filename.split(".")[0]?.trim() === ""
            const hasMandatoryFields = !nameIsEmpty && !!values.document_type && !!values.date && !!values.file
            const areInitialValues = isEqual(values, initialValues)

            return <Styled.Form>
                <ScrollableLayout
                    className={className}
                    content={
                        <Styled.FormContent isMobile={isMobile}>
                            <Styled.DropZone
                                isMobile={isMobile}
                                acceptedFileText={t(I18N_NAMESPACE, "forms.documentAddForm.acceptedFileText", { maxSize: MAX_FILE_SIZE_MB })}
                                multiple={false}
                                file={values.file}
                                validator={fileSizeValidator}
                                onDrop={(files) => handleDropFile(files, setFieldValue, values)}
                            />
                            <Styled.FieldsWrapper isMobile={isMobile}>
                                <Styled.TextField
                                    id="file-name"
                                    name="filename"
                                    type="text"
                                    label={`${t(I18N_NAMESPACE, "forms.documentAddForm.fileName.label")}*`}
                                    value={values.filename}
                                    onChange={(fileName) => updateFieldValue("filename", fileName, setFieldValue, values)}
                                />
                                <Styled.Select
                                    id="file-type"
                                    name="document_type"
                                    value={values.document_type || ""}
                                    label={`${t(I18N_NAMESPACE, "forms.documentAddForm.fileType")}*`}
                                    onChange={v => handleFieldChange({ "document_type": v }, values)}>
                                    {typeOptions}
                                </Styled.Select>
                                <Styled.DatePicker
                                    id="file-date"
                                    name="date"
                                    format="dd/MM/yyyy"
                                    label={`${t(I18N_NAMESPACE, "forms.documentAddForm.fileDate")}*`}
                                    value={values.date}
                                    onChange={(date, context) => handleDateChange(date, context, setFieldValue, values, setFieldError)}
                                />
                                <Styled.TagsAutocomplete
                                    name="tags"
                                    variant="outlined"
                                    tags={values.tags}
                                    existingTags={(project?.tags || [])}
                                    onChange={v => updateFieldValue("tags", v, setFieldValue, values)}
                                />
                            </Styled.FieldsWrapper>
                        </Styled.FormContent>}
                    buttons={
                        <>
                            <L2RButton
                                id="cancel-button"
                                onClick={() => {
                                    onCancel()
                                }}
                                variant="tertiary"
                            >
                                {t(I18N_NAMESPACE, "forms.documentAddForm.cancel")}
                            </L2RButton>
                            <Styled.SubmitButton
                                id="submit-button"
                                type="submit"
                                disabled={hasErrors || !hasMandatoryFields || isSubmitting || areInitialValues}
                            >
                                {t(I18N_NAMESPACE, "forms.documentAddForm.save")}
                            </Styled.SubmitButton>
                        </>
                    }
                />
            </Styled.Form>
        }}
    </Formik >
}

DocumentForm.propTypes = {
    className: PropTypes.string,
    document: PropTypes.object,
    isSubmitting: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
}